import {
  Background,
  type Edge,
  type EdgeTypes,
  MarkerType,
  MiniMap,
  type Node,
  type NodeTypes,
  Panel,
  ReactFlow,
  ReactFlowProvider,
} from "@xyflow/react";
import ELK, { type ElkNode } from "elkjs/lib/elk.bundled.js";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import type { ErdEdge, ErdNode, PipelineSurfaceStatus } from "@busright/data-contract";
import { COLORS, RAG } from "@busright/ui";

import { ChartCard } from "@/components/ChartCard";
import {
  DS_NODE_HEIGHT,
  DS_NODE_WIDTH,
  type DsFreshness,
  DsFreshnessNode,
  type DsFreshnessNodeData,
  dsFreshnessColor,
} from "@/components/DsFreshnessNode";
import { EmptyState } from "@/components/EmptyState";
import { useErd } from "@/hooks/useErd";

import "@xyflow/react/dist/style.css";

// elk partition index for a dataset, so the graph reads as dataset bands left→right. Datasets not
// in this map (shouldn't happen — the live graph is currently hardware_health / reporting /
// supply_chain) fall into a trailing column rather than being dropped.
const DATASET_ORDER: Record<string, number> = { hardware_health: 0, reporting: 1, supply_chain: 2 };
const DATASET_ORDER_FALLBACK = 3;
const partitionFor = (dataset: string): string =>
  String(DATASET_ORDER[dataset] ?? DATASET_ORDER_FALLBACK);

const elk = new ELK();

interface PositionedNode {
  readonly id: string;
  readonly x: number;
  readonly y: number;
}

// Run elk's layered layout left→right, partitioning columns by dataset so the datasets read as
// bands, for the LIVE node/edge set (all ~69+ objects from /api/erd, not a hardcoded sample).
const layout = async (
  nodes: readonly ErdNode[],
  edges: readonly ErdEdge[],
): Promise<readonly PositionedNode[]> => {
  const graph: ElkNode = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "RIGHT",
      "elk.layered.spacing.nodeNodeBetweenLayers": "120",
      "elk.spacing.nodeNode": "40",
      "elk.partitioning.activate": "true",
    },
    children: nodes.map((n) => ({
      id: n.id,
      width: DS_NODE_WIDTH,
      height: DS_NODE_HEIGHT,
      layoutOptions: { "elk.partitioning.partition": partitionFor(n.dataset) },
    })),
    edges: edges.map((e, i) => ({
      id: `edge-${i}`,
      sources: [e.source],
      targets: [e.target],
    })),
  };
  const result = await elk.layout(graph);
  return (result.children ?? []).map((c) => ({ id: c.id, x: c.x ?? 0, y: c.y ?? 0 }));
};

const nodeTypes: NodeTypes = { dsFreshness: DsFreshnessNode };
const edgeTypes: EdgeTypes = {};

// A live ERD node's own `freshness` (ISO instant or null) doesn't carry a staleness verdict by
// itself — reuse the pipeline-health classification when a matching curated surface exists (same
// FRESH/RECENT/STALE law the pipeline table uses), otherwise fall back to "has a freshness instant
// at all" as a weak signal, and "UNKNOWN" (no signal) when neither is available.
const freshnessBySurface = (
  surfaces: readonly PipelineSurfaceStatus[],
): Map<string, PipelineSurfaceStatus> => {
  const map = new Map<string, PipelineSurfaceStatus>();
  for (const s of surfaces) {
    map.set(s.surface, s);
  }
  return map;
};

const freshnessFor = (
  node: ErdNode,
  bySurface: Map<string, PipelineSurfaceStatus>,
): DsFreshness => {
  const status = bySurface.get(node.table);
  if (status) {
    return status.staleness;
  }
  return node.freshness ? "FRESH" : "UNKNOWN";
};

export interface DsErdCanvasProps {
  readonly surfaces: readonly PipelineSurfaceStatus[];
  readonly loading?: boolean;
  // Click a node to focus the raw explorer on that table. Optional — nodes are static if absent.
  readonly onFocusTable?: (table: string) => void;
}

const LegendDot = ({
  color,
  label,
  dashed,
}: {
  readonly color: string;
  readonly label: string;
  readonly dashed?: boolean;
}): ReactNode => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    {dashed ? (
      <span
        aria-hidden
        style={{
          width: 14,
          height: 0,
          borderTop: `2px dashed ${color}`,
        }}
      />
    ) : (
      <span aria-hidden style={{ width: 9, height: 9, borderRadius: "50%", background: color }} />
    )}
    <span style={{ fontSize: 11, color: "var(--br-text-secondary)" }}>{label}</span>
  </span>
);

const CONFIDENCE_TO_PCT = 100;
const edgeLabel = (e: ErdEdge): string =>
  typeof e.confidence === "number"
    ? `${e.key} (${Math.round(e.confidence * CONFIDENCE_TO_PCT)}%)`
    : e.key;

const toEdges = (edges: readonly ErdEdge[]): Edge[] => {
  return edges.map<Edge>((e, i) => {
    if (e.state === "broken") {
      return {
        id: `broken-${i}`,
        source: e.source,
        target: e.target,
        label: edgeLabel(e),
        labelStyle: { fill: RAG.CRITICAL, fontSize: 10, fontWeight: 600 },
        labelBgStyle: { fill: COLORS.surface0 },
        animated: true,
        style: { stroke: RAG.CRITICAL, strokeWidth: 1.5, strokeDasharray: "6 4" },
        markerEnd: { type: MarkerType.ArrowClosed, color: RAG.CRITICAL },
      };
    }
    if (e.state === "inferred") {
      return {
        id: `inferred-${i}`,
        source: e.source,
        target: e.target,
        label: edgeLabel(e),
        labelStyle: { fill: COLORS.textSecondary, fontSize: 10, fontStyle: "italic" },
        labelBgStyle: { fill: COLORS.surface0 },
        style: { stroke: COLORS.accent, strokeWidth: 1.25, strokeDasharray: "3 3" },
        markerEnd: { type: MarkerType.ArrowClosed, color: COLORS.accent },
      };
    }
    return {
      id: `real-${i}`,
      source: e.source,
      target: e.target,
      label: e.key,
      labelStyle: { fill: COLORS.textSecondary, fontSize: 10 },
      labelBgStyle: { fill: COLORS.surface0 },
      style: { stroke: COLORS.borderStrong, strokeWidth: 1.5 },
      markerEnd: { type: MarkerType.ArrowClosed, color: COLORS.borderStrong },
    };
  });
};

// Section 2 — pannable/zoomable, LIVE ERD from /api/erd (all ~69+ warehouse objects across the 3
// datasets, replacing the old hardcoded 12-table sample). Nodes colored by freshness; solid edges
// are real join keys; dashed amber edges are inferred/fuzzy joins (+confidence); dashed-red edges
// are joins that SHOULD exist but don't — that honesty is the point of the diagram.
const DsErdCanvasInner = ({ surfaces, loading, onFocusTable }: DsErdCanvasProps): ReactNode => {
  const erd = useErd();
  const graphNodes = useMemo(() => erd.data?.data.nodes ?? [], [erd.data]);
  const graphEdges = useMemo(() => erd.data?.data.edges ?? [], [erd.data]);

  const [positions, setPositions] = useState<readonly PositionedNode[]>([]);
  const [layoutError, setLayoutError] = useState(false);

  useEffect(() => {
    if (graphNodes.length === 0) {
      return;
    }
    let cancelled = false;
    setPositions([]);
    setLayoutError(false);
    layout(graphNodes, graphEdges)
      .then((pos) => {
        if (!cancelled) {
          setPositions(pos);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLayoutError(true);
        }
      });
    return (): void => {
      cancelled = true;
    };
  }, [graphNodes, graphEdges]);

  const nodes = useMemo<Node<DsFreshnessNodeData>[]>(() => {
    const posById = new Map(positions.map((p) => [p.id, p]));
    const bySurface = freshnessBySurface(surfaces);
    return graphNodes.map((n) => {
      const pos = posById.get(n.id);
      return {
        id: n.id,
        type: "dsFreshness",
        position: { x: pos?.x ?? 0, y: pos?.y ?? 0 },
        data: {
          table: n.table,
          dataset: n.dataset,
          freshness: freshnessFor(n, bySurface),
          cadence: n.cadence,
          rowCount: n.rowCount ?? null,
        },
      };
    });
  }, [positions, surfaces, graphNodes]);

  const edges = useMemo<Edge[]>(() => toEdges(graphEdges), [graphEdges]);

  const isLoading = loading || erd.isLoading;

  if (erd.isError) {
    return (
      <ChartCard title="Entity-relationship map">
        <EmptyState
          title="ERD unavailable"
          message={
            erd.error instanceof Error
              ? erd.error.message
              : "Could not load the entity-relationship graph from the API."
          }
        />
      </ChartCard>
    );
  }

  if (isLoading || (graphNodes.length > 0 && positions.length === 0 && !layoutError)) {
    return (
      <ChartCard title="Entity-relationship map" loading>
        <div />
      </ChartCard>
    );
  }

  if (layoutError) {
    return (
      <ChartCard title="Entity-relationship map">
        <EmptyState
          title="Layout failed"
          message="The ERD auto-layout engine could not run in this browser."
        />
      </ChartCard>
    );
  }

  if (graphNodes.length === 0) {
    return (
      <ChartCard title="Entity-relationship map">
        <EmptyState title="No graph data" message="The ERD endpoint returned no nodes." />
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title="Entity-relationship map"
      subtitle={
        onFocusTable
          ? `${graphNodes.length} objects · solid = real join · dashed amber = inferred (fuzzy) · dashed red = missing join · click a node to preview it below`
          : `${graphNodes.length} objects · solid = real join · dashed amber = inferred (fuzzy) · dashed red = missing join`
      }
      source="@xyflow/react · elkjs · /api/erd"
    >
      <div
        style={{
          height: 620,
          border: "1px solid var(--br-border)",
          borderRadius: "var(--br-radius-md)",
          overflow: "hidden",
          background: COLORS.surface0,
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          minZoom={0.05}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          onNodeClick={
            onFocusTable
              ? (_event, node: Node<DsFreshnessNodeData>) => onFocusTable(node.data.table)
              : undefined
          }
        >
          <Background color={COLORS.border} gap={20} />
          <MiniMap
            pannable
            zoomable
            style={{ background: COLORS.surface1 }}
            maskColor="rgba(10,14,26,0.7)"
            nodeColor={(node) => {
              const data = node.data as DsFreshnessNodeData;
              return dsFreshnessColor(data.freshness);
            }}
          />
          <Panel position="top-left">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                padding: "7px 11px",
                background: "var(--br-surface-1)",
                border: "1px solid var(--br-border)",
                borderRadius: "var(--br-radius-md)",
              }}
            >
              <LegendDot color={RAG.OK} label="Fresh" />
              <LegendDot color={RAG.WARN} label="Recent" />
              <LegendDot color={RAG.CRITICAL} label="Stale" />
              <LegendDot color={COLORS.textMuted} label="No signal" />
              <LegendDot color={COLORS.accent} label="Inferred join" dashed />
              <LegendDot color={RAG.CRITICAL} label="Broken / missing join" dashed />
            </div>
          </Panel>
        </ReactFlow>
      </div>
    </ChartCard>
  );
};

export const DsErdCanvas = (props: DsErdCanvasProps): ReactNode => (
  <ReactFlowProvider>
    <DsErdCanvasInner {...props} />
  </ReactFlowProvider>
);
